import { Button } from '../../common/Button';
import { useStore } from 'app/context';
import { JpgThema } from 'app/stores/bnr.document.store';
import { Formik, Form, Field, useFormikContext } from 'formik';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import FormField from '../../form/FormField';
import { Modal } from '../../Modal';

import * as Yup from 'yup';
import { AdZJpgLink, JpgHandlungsempfehlungen } from './JpgThemenContent';
import { AgenturModel } from 'app/models/agentur.model';
import { useEffect, useState, useMemo } from 'react';
import { BranchenplanungPosDef } from 'app/models/branchenplanung/bp.pos.def';
import { AgtBpVM } from 'app/stores/ui/agt.branchenplanung.ui.store';
import _ from 'lodash';
import { AgtBranchenplanungKeysField } from 'app/components/anwendungen/branchenplanung/AgtBranchenplanungKeysField';
import { Jpg25DocumentShareFocusThemaModal } from './Jpg25DocumentShareFocusThemaModal';

interface IThemaEditModal {
	onChange: () => void;
	onDelete: () => void;
	agt?: AgenturModel;
	year?: number;
}

export const JpgThemaEditModal = observer((props: IThemaEditModal) => {
	const { uiStore, documentUiStore, jpgThemenUiStore } = useStore();
	const f = documentUiStore.currentFocusThemaEdit;
	const isNew = documentUiStore.currentFocusThemaEditIsNew;

	// Use props if provided, otherwise fall back to store
	const agt = props.agt || documentUiStore.current?.agt;
	const year = props.year || documentUiStore.current?.planYear;

	// Early return if we don't have required data
	if (!agt || !year) {
		return null;
	}

	const themen = jpgThemenUiStore.getThemenByYear(year);
	const def = themen.find((t) => t.keyThema === f?.key);

	const onCancel = () => {
		uiStore.hideModal(uiStore.modalIds.jpgThemaEdit);
	};
	return (
		<Modal modalId={uiStore.modalIds.jpgThemaEdit} title={def ? def.thema : 'Kein Thema'} size={'large'} modifier="is-form-modal is-full-height">
			{def && f && (
				<>
					<JpgFocusThemaEdit thema={f} onChange={props.onChange} onCancel={onCancel} onDelete={props.onDelete} isNew={isNew} def={def} agt={agt} year={year} />
				</>
			)}
		</Modal>
	);
});

interface IJpgThemaEdit {
	thema: JpgThema;
	isNew: boolean;
	onChange: () => void;
	onCancel: () => void;
	onDelete: () => void;
	def: import('app/stores/ui/jpg.themen.ui.store').IJpgThemaDef;
	agt: AgenturModel;
	year: number;
	children?: React.ReactNode;
}
const defaultFormType = Yup.number()
	.transform((value, originalValue) => {
		return Number.isNaN(value) ? 0 : value;
	})
	.nullable()
	.default(0)
	.typeError('Nur numerische Werte sind erlaubt');

export const JpgFocusThemaEdit = observer((props: IJpgThemaEdit) => {
	const { uiStore, agtBranchenplanungUiStore } = useStore();
	const [bpData, setBpData] = useState<AgtBpVM>();
	const [initialBpValues, setInitialBpValues] = useState<any>();
	const { def, agt, year } = props;

	// Load BP data if bpKeys are defined
	useEffect(() => {
		if (props.def.bpKeys && props.def.bpKeys.length > 0) {
			agtBranchenplanungUiStore.findByYearAndAgtId(props.year, props.agt.agtId).then((d) => {
				if (!d) {
					d = agtBranchenplanungUiStore.createEmptyBP(props.agt, props.year);
				}
				setBpData(d);

				// Store initial values for change detection
				const initial: any = {};
				props.def.bpKeys!.forEach((key) => {
					initial[key] = (d as any)[key];
				});
				setInitialBpValues(initial);
			});
		}
	}, [agtBranchenplanungUiStore, props.year, props.agt.agtId, props.def.bpKeys]);

	// Create dynamic schema based on bpKeys
	const jpgThemaEditSchema = useMemo(() => {
		const schemaShape: any = {
			notiz: Yup.string(),
		};

		if (props.def.bpKeys) {
			props.def.bpKeys.forEach((key) => {
				schemaShape[key] = defaultFormType;
			});
		}

		return Yup.object(schemaShape);
	}, [props.def.bpKeys]);

	const handleSubmit = async (values: any) => {
		runInAction(() => {
			props.thema.notiz = values.notiz ? values.notiz : '';
			props.thema.share = values.share ? values.share : [];
		});

		// Handle BP keys if they exist and values changed
		if (props.def.bpKeys && bpData && initialBpValues) {
			const hasChanges = props.def.bpKeys.some((key) => {
				const newValue = values[key] ? parseFloat(values[key]) : null;
				const oldValue = initialBpValues[key];
				return newValue !== oldValue;
			});

			if (hasChanges) {
				const visiblePosDefs = BranchenplanungPosDef.filter((p) => props.def.bpKeys!.includes(p.planKey));

				visiblePosDefs.forEach((bpPosDef) => {
					const prop = bpPosDef.prop;
					runInAction(() => {
						let v = (values as any)[prop];
						if (v) {
							let v2 = parseFloat(v);
							(bpData as any)[prop] = v2;
						} else {
							(bpData as any)[prop] = null;
						}
					});
				});

				await agtBranchenplanungUiStore.saveMany([bpData]);
			}
		}

		props.onChange();
		uiStore.hideModal(uiStore.modalIds.jpgThemaEdit);
	};

	const onDelete = () => {
		if (window.confirm('Sind Sie sicher, dass Sie das Fokusthema löschen möchten?')) {
			props.onDelete();
			uiStore.hideModal(uiStore.modalIds.jpgThemaEdit);
		}
	};

	// Prepare initial values
	const getInitialValues = () => {
		const values: any = {
			notiz: props.thema.notiz ? props.thema.notiz : '',
			share: props.thema.share ? props.thema.share : [],
		};

		// Add BP values if they exist
		if (props.def.bpKeys && bpData) {
			props.def.bpKeys.forEach((key) => {
				values[key] = (bpData as any)[key] || '';
			});
		}

		return values;
	};

	return (
		<>
			<Formik validationSchema={jpgThemaEditSchema} initialValues={getInitialValues()} onSubmit={handleSubmit} enableReinitialize={true}>
				{({ values, errors, touched, isValid }) => {
					return (
						<Form>
							<div className="grid is-col-auto is-col-2-1 min-width-1000 full-height">
								<div className="grid full-height align-start">
									{['ADZ', 'Aktvitätsschwerpunkte'].includes(def.group) && <AdZJpgLink />}

									{def.handlungsEmpfehlungen && (
										<div className="handlungsempfehlungen">
											<JpgHandlungsempfehlungen handlungsEmpfehlungen={def.handlungsEmpfehlungen} />
										</div>
									)}

									{props.def.bpKeys && props.def.bpKeys.length > 0 && bpData && (
										<div className="grid pad-top-1rem bp-fields">
											<AgtBranchenplanungKeysField agt={props.agt} year={props.year} bpKeys={props.def.bpKeys} editMode={true} />
										</div>
									)}

									{def.dataComponent && (
										<>
											<div className="data-component grid">
												<div className="pad-1rem pad-bottom-0rem">
													<strong>Übersicht Daten</strong>
												</div>
												{def.dataComponent(agt, year)}
											</div>
										</>
									)}

									{def.textComponent && <div className="text-component">{def.textComponent}</div>}

									{props.children && props.children}
								</div>
								<div className="grid full-height">
									<FormField error={errors.notiz as string} touched={touched.notiz as boolean} label="Notiz" className="jpg-notiz full-height">
										<Field className="input" type="text" as="textarea" name="notiz" placeholder="Notiz" />
									</FormField>
								</div>
							</div>

							<div className="modal-footer">
								<Button type="button" className="button is-secondary" onClick={props.onCancel}>
									Abbrechen
								</Button>
								{!props.isNew && (
									<Button type="button" className="button is-danger is-inverted" onClick={onDelete}>
										Fokusthema entfernen
									</Button>
								)}
								<Button type="submit" className="button is-primary ">
									{props.isNew ? <>Fokusthema anlegen</> : <>Fokusthema speichern</>}
								</Button>
							</div>

							<Jpg25DocumentShareFocusThemaModal def={props.def} agt={props.agt} year={props.year} />
						</Form>
					);
				}}
			</Formik>
		</>
	);
});
